import { Replace, SnakeIdObj } from '@synapex/tools/typescript';

import * as Entity from '../../entity';
import * as Selector from '../../entity/selector/all';

import {
  MutateOpts,
  UseMutationOpts,
  useMutationOptimistic,
  useMutationSuccessful,
} from '../../../engine';
import {
  create,
  createSite,
  update,
  putNewAccountChannelRequest,
  putChannelRequest,
  deleteChannelRequest,
} from '../../api-client/all';
import { mapAccount } from '../cache/all';
import { all } from '../key-fn';

///////////////

export type CreateOpts = MutateOpts<typeof create, typeof all>;

export const useCreate = ({
  onSuccess,
  ...opts
}: Replace<
  UseMutationOpts<CreateOpts>,
  'onSuccess',
  {
    onSuccess?: (result: {
      createdAccount: Entity.AdvertiserAccount;
      invite: Entity.Invite;
    }) => void;
  }
>) => {
  const queryKey = all().queryKey;

  return useMutationSuccessful(
    {
      queryKey,
      mutationKey: queryKey,
      mutationFn: create(),
      successful: (data, { invite: _, ...accountCreated }) => {
        return {
          ...data,
          advertiserAccounts: [accountCreated, ...data.advertiserAccounts],
        };
      },
    },
    {
      ...opts,
      onSuccess(createdAccount, ..._args) {
        if (onSuccess) {
          onSuccess(Selector.createdAccountAndInvite(createdAccount));
        }
      },
    },
  );
};

///////////////

export type UpdateOpts = MutateOpts<typeof update, typeof all>;

export const useUpdate = (
  p: SnakeIdObj<'advertiser_account'>,
  opts?: UseMutationOpts<UpdateOpts>,
) => {
  const queryKey = all().queryKey;

  return useMutationOptimistic(
    {
      queryKey,
      mutationKey: [queryKey[0], p.advertiser_account_id],
      mutationFn: update(p),
      variablesValid: (variables) => Boolean(variables.name),
      optimistic: (data, variables) =>
        mapAccount(data, p, (acc) => ({
          ...acc,
          variables,
        })),
    },
    opts,
  );
};

///////////////

export type CreateSiteOpts = MutateOpts<typeof createSite, typeof all>;

export const useCreateSite = (
  p: SnakeIdObj<'advertiser_account'>,
  opts?: UseMutationOpts<CreateSiteOpts>,
) => {
  const queryKey = all().queryKey;

  return useMutationSuccessful(
    {
      queryKey,
      mutationKey: [queryKey[0], p.advertiser_account_id],
      mutationFn: createSite(p),
      successful: (data, siteCreated) =>
        mapAccount(data, p, (acc) => ({
          ...acc,
          workspaces: [siteCreated, ...acc.workspaces],
        })),
    },
    opts,
  );
};

///////////////

export type PutNewAccountChannelRequestOpts = MutateOpts<
  typeof putNewAccountChannelRequest,
  typeof all
>;

export const usePutNewAccountChannelRequest = (
  opts: UseMutationOpts<PutNewAccountChannelRequestOpts> = {},
) => {
  const queryKey = all().queryKey;

  return useMutationSuccessful(
    {
      queryKey,
      mutationKey: queryKey,
      mutationFn: putNewAccountChannelRequest(),
      successful: (data, channelRequestCreated) => ({
        ...data,
        channelRequests: [channelRequestCreated, ...data.channelRequests],
      }),
    },
    opts,
  );
};

///////////////

export type PutChannelRequestOpts = MutateOpts<
  typeof putChannelRequest,
  typeof all
>;

export const usePutChannelRequest = (
  p: SnakeIdObj<'advertiser_account'>,
  opts?: UseMutationOpts<PutChannelRequestOpts>,
) => {
  const queryKey = all().queryKey;

  return useMutationSuccessful(
    {
      queryKey,
      mutationKey: [queryKey[0], p.advertiser_account_id],
      mutationFn: putChannelRequest(p),
      successful: (data, channelRequestCreated) =>
        mapAccount(data, p, (acc) => ({
          ...acc,
          channelRequests: [channelRequestCreated, ...acc.channelRequests],
        })),
    },
    opts,
  );
};

///////////////

export type DeleteChannelRequestOpts = MutateOpts<
  typeof deleteChannelRequest,
  typeof all
>;

export const useDeleteChannelRequest = (
  {
    telegram_channel_id,
    advertiser_account_id,
  }: {
    telegram_channel_id: string;
    advertiser_account_id?: number;
  },
  opts: UseMutationOpts<DeleteChannelRequestOpts> = {},
) => {
  const queryKey = all().queryKey;

  return useMutationSuccessful(
    {
      queryKey,
      mutationKey: queryKey,
      mutationFn: deleteChannelRequest({ telegram_channel_id }),
      successful: (data) =>
        advertiser_account_id
          ? mapAccount(data, { advertiser_account_id }, (acc) => ({
              ...acc,
              channelRequests: acc.channelRequests.filter(
                (cr) => cr.telegram_channel_id !== telegram_channel_id,
              ),
            }))
          : {
              ...data,
              channelRequests: data.channelRequests.filter(
                (cr) => cr.telegram_channel_id !== telegram_channel_id,
              ),
            },
    },
    opts,
  );
};
